import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { Project } from '@chronos/shared';
import { apiFetch } from '@/api/client';
import { useProjects, useUsers } from '@/hooks/queries';
import { Card, ConfirmButton, Dot, EmptyState, ErrorNote, Modal, PageHeader, Spinner, SubmitButton } from '@/components/ui';
import { formatDate } from '@/lib/format';

const PALETTE = ['#3182f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#64748b'];

export function ProjectsPage() {
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [editing, setEditing] = useState<Project | null>(null);
  const [creating, setCreating] = useState(false);
  const [members, setMembers] = useState<Project | null>(null);

  const { data, isLoading, error } = useProjects({ q: q || undefined, status: status || undefined, pageSize: 200 });
  const qc = useQueryClient();

  const remove = useMutation({
    mutationFn: (id: string) => apiFetch<void>(`/api/projects/${id}`, { method: 'DELETE' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['projects'] }),
  });

  const archive = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'ACTIVE' | 'ARCHIVED' }) =>
      apiFetch<Project>(`/api/projects/${id}`, { method: 'PATCH', body: { status } }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['projects'] }),
  });

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader
        title="Projects"
        description="Create projects, set billing rates, and choose who can track time against them."
        actions={<button className="btn-primary" onClick={() => setCreating(true)}>New project</button>}
      />

      <div className="mb-4 flex flex-wrap gap-2">
        <input className="input max-w-xs" placeholder="Search name or client" value={q} onChange={(e) => setQ(e.target.value)} />
        <select className="input max-w-[10rem]" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All projects</option>
          <option value="ACTIVE">Active</option>
          <option value="ARCHIVED">Archived</option>
        </select>
      </div>

      <ErrorNote error={error ?? remove.error ?? archive.error} />

      {isLoading ? (
        <div className="flex justify-center py-16"><Spinner /></div>
      ) : (data?.items.length ?? 0) === 0 ? (
        <Card><EmptyState title="No projects yet" description="Create one to let your team start tracking time." /></Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {data?.items.map((p) => (
            <Card key={p.id} className="flex flex-col p-4">
              <div className="flex items-start gap-2">
                <Dot color={p.color} />
                <div className="min-w-0 flex-1">
                  <h3 className="truncate font-medium text-slate-900">{p.name}</h3>
                  {p.client && <p className="truncate text-xs text-slate-500">{p.client}</p>}
                </div>
                {p.status === 'ARCHIVED' && <span className="badge bg-slate-200 text-slate-600">archived</span>}
              </div>

              {p.description && <p className="mt-2 line-clamp-2 text-sm text-slate-600">{p.description}</p>}

              <dl className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
                <div><dt className="inline">Members: </dt><dd className="inline font-medium text-slate-700">{p.memberCount ?? 0}</dd></div>
                <div><dt className="inline">Rate: </dt><dd className="inline font-medium text-slate-700">{p.hourlyRate === null ? 'none' : `${p.hourlyRate}/h`}</dd></div>
                <div><dt className="inline">Created: </dt><dd className="inline">{formatDate(p.createdAt)}</dd></div>
              </dl>

              <div className="mt-3 flex flex-wrap gap-1 border-t border-slate-100 pt-3">
                <button className="btn-ghost px-2 py-1 text-xs" onClick={() => setEditing(p)}>Edit</button>
                <button className="btn-ghost px-2 py-1 text-xs" onClick={() => setMembers(p)}>Members</button>
                <button
                  className="btn-ghost px-2 py-1 text-xs"
                  onClick={() => archive.mutate({ id: p.id, status: p.status === 'ACTIVE' ? 'ARCHIVED' : 'ACTIVE' })}
                >
                  {p.status === 'ACTIVE' ? 'Archive' : 'Restore'}
                </button>
                <ConfirmButton className="btn-ghost ml-auto px-2 py-1 text-xs text-rose-600" label="Delete" confirmLabel="Confirm" onConfirm={() => remove.mutate(p.id)} />
              </div>
            </Card>
          ))}
        </div>
      )}

      {creating && <ProjectModal onClose={() => setCreating(false)} />}
      {editing && <ProjectModal key={editing.id} project={editing} onClose={() => setEditing(null)} />}
      {members && <MembersModal key={`m-${members.id}`} project={members} onClose={() => setMembers(null)} />}
    </div>
  );
}

/** Active users holding ADMIN or MANAGER — the people put on a project by default. */
function useStaffIds(): string[] {
  const users = useUsers({ status: 'ACTIVE' });
  return useMemo(
    () =>
      (users.data?.items ?? [])
        .filter((u) => u.roles.includes('ADMIN') || u.roles.includes('MANAGER'))
        .map((u) => u.id),
    [users.data],
  );
}

function ProjectModal({ project, onClose }: { project?: Project; onClose: () => void }) {
  const qc = useQueryClient();
  const staffIds = useStaffIds();
  const [form, setForm] = useState({
    name: project?.name ?? '',
    description: project?.description ?? '',
    client: project?.client ?? '',
    color: project?.color ?? PALETTE[0],
    hourlyRate: project?.hourlyRate === null || project?.hourlyRate === undefined ? '' : String(project.hourlyRate),
  });

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: form.name,
        description: form.description || null,
        client: form.client || null,
        color: form.color,
        hourlyRate: form.hourlyRate === '' ? null : Number(form.hourlyRate),
      };
      return project
        ? apiFetch<Project>(`/api/projects/${project.id}`, { method: 'PATCH', body })
        // New projects start with every admin and manager assigned, as plain
        // members: they can track time against it immediately. Promoting
        // someone to project manager stays deliberate, since that flag is what
        // grants sight of the team's hours and screenshots.
        : apiFetch<Project>('/api/projects', { method: 'POST', body: { ...body, memberIds: staffIds } });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['projects'] });
      onClose();
    },
  });

  return (
    <Modal
      open
      title={project ? `Edit ${project.name}` : 'New project'}
      onClose={onClose}
      footer={
        <>
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <SubmitButton form="project-form" pending={save.isPending} className="btn-primary">{project ? 'Save changes' : 'Create project'}</SubmitButton>
        </>
      }
    >
      <form id="project-form" className="space-y-4" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
        <ErrorNote error={save.error} />
        {!project && staffIds.length > 0 && (
          <p className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
Admins and managers ({staffIds.length}) are members of every project automatically. Use{' '}
            <span className="font-medium">Members</span> afterwards to add the rest of the team.
          </p>
        )}
        <div>
          <label className="label" htmlFor="pname">Project name</label>
          <input id="pname" className="input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="label" htmlFor="pclient">Client <span className="font-normal text-slate-400">(optional)</span></label>
            <input id="pclient" className="input" value={form.client} onChange={(e) => setForm({ ...form, client: e.target.value })} />
          </div>
          <div>
            <label className="label" htmlFor="prate">Hourly rate <span className="font-normal text-slate-400">(optional)</span></label>
            <input id="prate" type="number" min="0" step="0.01" className="input" value={form.hourlyRate} onChange={(e) => setForm({ ...form, hourlyRate: e.target.value })} />
          </div>
        </div>
        <div>
          <label className="label" htmlFor="pdesc">Description</label>
          <textarea id="pdesc" className="input min-h-[80px]" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} maxLength={2000} />
        </div>
        <div>
          <span className="label">Colour</span>
          <div className="flex flex-wrap gap-2">
            {PALETTE.map((c) => (
              <button
                type="button"
                key={c}
                aria-label={`Colour ${c}`}
                onClick={() => setForm({ ...form, color: c })}
                className={`h-8 w-8 rounded-full border-2 transition ${form.color === c ? 'border-slate-900 scale-110' : 'border-transparent'}`}
                style={{ backgroundColor: c }}
              />
            ))}
          </div>
        </div>
      </form>
    </Modal>
  );
}

function MembersModal({ project, onClose }: { project: Project; onClose: () => void }) {
  const users = useUsers({ status: 'ACTIVE' });
  const staffIds = useStaffIds();
  const qc = useQueryClient();
  const [assigned, setAssigned] = useState<Record<string, 'member' | 'manager'>>(() =>
    Object.fromEntries((project.members ?? []).map((m) => [m.userId, m.isManager ? 'manager' : 'member'])),
  );
  // Admins and managers belong to every project and the server enforces it, so
  // show them ticked even before the list has caught up rather than offering a
  // checkbox whose unticking would be undone on save.
  useEffect(() => {
    if (staffIds.length === 0) return;
    setAssigned((prev) => {
      const missing = staffIds.filter((id) => !prev[id]);
      if (missing.length === 0) return prev;
      return { ...prev, ...Object.fromEntries(missing.map((id) => [id, 'member' as const])) };
    });
  }, [staffIds]);

  const save = useMutation({
    mutationFn: () =>
      apiFetch<Project>(`/api/projects/${project.id}/members`, {
        method: 'PUT',
        body: {
          memberIds: Object.keys(assigned),
          managerIds: Object.entries(assigned).filter(([, role]) => role === 'manager').map(([id]) => id),
        },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['projects'] });
      onClose();
    },
  });

  return (
    <Modal
      open
      wide
      title={`Members of ${project.name}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <SubmitButton pending={save.isPending} className="btn-primary" onClick={() => save.mutate()} type="button">Save assignments</SubmitButton>
        </>
      }
    >
      <ErrorNote error={save.error} />
      <p className="mb-3 text-sm text-slate-500">
        Assigned people can track time against this project. Marking someone a <span className="font-medium">manager</span> lets them
        (if they hold the Manager role) see this project's reports and screenshots. Admins and managers are members of every
        project automatically.
      </p>
      {users.isLoading ? (
        <div className="flex justify-center py-8"><Spinner /></div>
      ) : (
        <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
          {users.data?.items.map((u) => {
            const role = assigned[u.id];
            return (
              <li key={u.id} className="flex items-center gap-3 px-3 py-2.5">
                <input
                  type="checkbox"
                  id={`m-${u.id}`}
                  className="h-4 w-4 rounded border-slate-300 disabled:opacity-60"
                  checked={!!role}
                  disabled={staffIds.includes(u.id)}
                  title={staffIds.includes(u.id) ? 'Admins and managers are members of every project' : undefined}
                  onChange={(e) =>
                    setAssigned((prev) => {
                      const next = { ...prev };
                      if (e.target.checked) next[u.id] = 'member';
                      else delete next[u.id];
                      return next;
                    })
                  }
                />
                <label htmlFor={`m-${u.id}`} className="min-w-0 flex-1 cursor-pointer">
                  <span className="block truncate text-sm font-medium text-slate-800">
                    {u.name}
                    {staffIds.includes(u.id) && <span className="ml-2 text-xs font-normal text-slate-400">always a member</span>}
                  </span>
                  <span className="block truncate text-xs text-slate-500">{u.email}</span>
                </label>
                {role && (
                  <select
                    className="input max-w-[9rem] py-1 text-xs"
                    value={role}
                    onChange={(e) => setAssigned((prev) => ({ ...prev, [u.id]: e.target.value as 'member' | 'manager' }))}
                  >
                    <option value="member">Member</option>
                    <option value="manager" disabled={!u.roles.includes('MANAGER')}>
                      Manager{u.roles.includes('MANAGER') ? '' : ' (needs Manager role)'}
                    </option>
                  </select>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Modal>
  );
}
